import { auth } from '../firebase';
import { TALENT_API_BASE } from '../components/TalentSearch/talentAccount';

// All mounted cards share one queue: translate distinct position titles in a
// batch, never one HTTP request per candidate. The server shares its saved
// translations across employers. Names, companies and CVs never enter here.
const saved = new Map();
const waiting = new Map();
const inFlight = new Map();
const retryAfter = new Map();
let timer;
let running = false;

async function flush() {
  timer = null;
  if (running || !waiting.size) return;
  running = true;
  const batch = [...waiting.keys()].slice(0, 100);
  const callbacks = batch.map(title => [title, waiting.get(title)]);
  batch.forEach(title => waiting.delete(title));
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  let translations = {};
  try {
    const token = await auth.currentUser.getIdToken();
    const response = await fetch(`${TALENT_API_BASE}/v2/display_titles`, {
      method: 'POST', signal: controller.signal,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ titles: batch }),
    });
    if (!response.ok) throw new Error('Title translations unavailable');
    translations = (await response.json()).translations || {};
  } catch {
    // Keep the source title readable if translation is temporarily unavailable.
  } finally {
    clearTimeout(timeout);
    callbacks.forEach(([title, resolvers]) => {
      const translated = typeof translations[title] === 'string' ? translations[title].trim() : '';
      if (translated) saved.set(title, translated);
      else retryAfter.set(title, Date.now() + 60000);
      inFlight.delete(title);
      resolvers.forEach(resolve => resolve(translated || title));
    });
    while (saved.size > 2000) saved.delete(saved.keys().next().value);
    while (retryAfter.size > 2000) retryAfter.delete(retryAfter.keys().next().value);
    running = false;
    if (waiting.size && !timer) timer = setTimeout(flush, 25);
  }
}

export function translatePosition(title) {
  if (!title || !auth?.currentUser || title.length > 240 || /[\u0600-\u06ff]/.test(title)) return Promise.resolve(title);
  if (saved.has(title)) return Promise.resolve(saved.get(title));
  if (inFlight.has(title)) return inFlight.get(title);
  if ((retryAfter.get(title) || 0) > Date.now()) return Promise.resolve(title);
  const result = new Promise(resolve => {
    if (!waiting.has(title)) waiting.set(title, []);
    waiting.get(title).push(resolve);
    if (!timer && !running) timer = setTimeout(flush, 25);
  });
  inFlight.set(title, result);
  return result;
}
